/**
 * OAuth callback page - handles redirect from OAuth providers.
 *
 * Exchanges the authorization code for tokens and redirects to channels page.
 * For YouTube: shows a channel picker before connecting.
 */

import {
  OAUTH_RETURN_URL_KEY,
  STATE_EXPIRED_MESSAGE,
  STATE_EXPIRED_TOKEN,
  useChannelCallback,
} from "@/lib/queries/channel";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import { getUserFriendlyOAuthError } from "@/lib/utils/oauth-errors";
import { OAuthCallbackCard } from "@/components/channels/oauth-callback-card";
import { YouTubeChannelPicker } from "@/components/channels/youtube-channel-picker";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import type { OrgRouteContext } from "../route";

const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export const Route = createFileRoute("/(org)/o/$orgSlug/oauth/callback")({
  validateSearch: searchSchema,
  component: OAuthCallbackPage,
});

export function OAuthCallbackPage() {
  const context = Route.useRouteContext() as OrgRouteContext;
  const { organization } = context;
  const { code, state, error, error_description } = Route.useSearch();
  const navigate = useNavigate();

  // Hooks must be called unconditionally. Pass a safe fallback to handle the
  // edge case where the parent route's beforeLoad guarantee has broken
  // (EPD-6938). The mutation is only triggered inside the guarded effect
  // below, so an empty organizationId never reaches the network.
  const callbackMutation = useChannelCallback(organization?.id);
  const hasProcessedRef = useRef(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard against double-submit: auth codes are single-use, so track processed codes
  // in sessionStorage to survive remounts on mobile browsers
  const codeKey = code ? `oauth_processed_${code.slice(0, 16)}` : null;

  interface StatePayload {
    profileId: string;
    organizationId: string;
    platform: string;
  }

  const statePayload = useMemo((): StatePayload | null => {
    if (!state) return null;
    try {
      const [payloadBase64] = state.split(".");
      const payloadJson = atob(payloadBase64);
      return JSON.parse(payloadJson) as StatePayload;
    } catch {
      return null;
    }
  }, [state]);

  type ChannelSelection = {
    channels: Array<{
      platformAccountId: string;
      displayName: string;
      handle?: string;
      avatarUrl?: string;
      profileUrl?: string;
    }>;
    tokenBundle: string;
    profileId: string;
  };

  // State for YouTube channel picker — lazy-initialized from sessionStorage so iOS
  // Safari page reloads mid-picker restore the picker without a useEffect setState call.
  const [channelSelection, setChannelSelection] =
    useState<ChannelSelection | null>(() => {
      if (!codeKey) return null;
      const stored = sessionStorage.getItem(`${codeKey}_yt`);
      if (!stored) return null;
      try {
        return JSON.parse(stored) as ChannelSelection;
      } catch {
        return null;
      }
    });
  const [pickerError, setPickerError] = useState<string | null>(null);

  // Determine status from URL params and mutation state
  const hasOAuthError = Boolean(error);
  const hasMissingParams = !code || !state;
  const urlErrorMessage = error_description || error || null;

  // Detect double-submit: auth codes are single-use and resubmitting a
  // redeemed code returns invalid_grant. Mobile browsers (especially iOS)
  // can remount components on background/foreground transitions.
  //
  // Gate behind !hasProcessedRef.current so the *current* mount's effect —
  // which sets sessionStorage[codeKey] = "1" before the mutation settles —
  // doesn't trip the "already completed" status branch on its own next
  // render. hasProcessedRef resets per mount but sessionStorage persists,
  // so true remounts (iOS Safari background/foreground) still get caught.
  const isAlreadyProcessed =
    !hasProcessedRef.current &&
    Boolean(codeKey && sessionStorage.getItem(codeKey));

  // Helper to redirect after successful connection
  const redirectToProfiles = () => {
    const returnKey = state
      ? `${OAUTH_RETURN_URL_KEY}:${state}`
      : OAUTH_RETURN_URL_KEY;
    const returnUrl = localStorage.getItem(returnKey);
    localStorage.removeItem(returnKey);

    const isSafeReturn =
      returnUrl &&
      returnUrl.startsWith("/") &&
      !returnUrl.startsWith("//") &&
      !returnUrl.includes("://");

    if (codeKey) {
      sessionStorage.removeItem(codeKey);
      sessionStorage.removeItem(`${codeKey}_yt`);
    }

    redirectTimeoutRef.current = setTimeout(() => {
      if (isSafeReturn) {
        navigate({ to: returnUrl });
      } else if (organization) {
        navigate({
          to: "/o/$orgSlug/profiles",
          params: { orgSlug: organization.slug },
        });
      } else {
        navigate({ to: "/" });
      }
    }, 2000);
  };

  // Trigger mutation when we have valid params
  useEffect(() => {
    if (hasProcessedRef.current || hasOAuthError || hasMissingParams) {
      return;
    }
    if (!code || !state) return;
    if (isAlreadyProcessed) return;
    // EPD-6938: bail BEFORE marking hasProcessedRef / sessionStorage so the
    // effect can retry on the next render when organization context loads.
    // Marking "processed" with a null org would permanently lock out the
    // OAuth code exchange — auth codes are single-use, sessionStorage
    // persists across renders, and the early-return branches above would
    // then short-circuit the retry attempt.
    if (!organization) return;

    hasProcessedRef.current = true;
    if (codeKey) sessionStorage.setItem(codeKey, "1");

    callbackMutation.mutate(
      { organizationId: organization.id, code, state },
      {
        onSuccess: (data) => {
          if (data.type === "channel_selection_required") {
            const sel = {
              channels: data.channels,
              tokenBundle: data.tokenBundle,
              profileId: statePayload?.profileId ?? "",
            };
            // Persist picker state so iOS Safari reloads can restore the picker
            if (codeKey)
              sessionStorage.setItem(`${codeKey}_yt`, JSON.stringify(sel));
            setChannelSelection(sel);
          } else {
            redirectToProfiles();
          }
        },
        onError: () => {
          // Clean up so user can retry
          if (codeKey) {
            sessionStorage.removeItem(codeKey);
            sessionStorage.removeItem(`${codeKey}_yt`);
          }
        },
      },
    );

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [
    code,
    state,
    statePayload,
    hasOAuthError,
    hasMissingParams,
    isAlreadyProcessed,
    navigate,
    organization?.id,
    organization?.slug,
    codeKey,
  ]);

  // iOS Safari reloads the callback page mid-flight, setting isAlreadyProcessed = true
  // before the API call completes. If the YouTube picker was active, the lazy useState
  // initializer already restored it from sessionStorage. For non-picker flows, redirect
  // to the stored returnUrl if one exists from the previous successful attempt.
  useEffect(() => {
    if (!isAlreadyProcessed) return;
    if (channelSelection) return; // picker already restored — nothing to do
    const returnKey = state
      ? `${OAUTH_RETURN_URL_KEY}:${state}`
      : OAUTH_RETURN_URL_KEY;
    const returnUrl = localStorage.getItem(returnKey);
    const isSafe =
      !!returnUrl &&
      returnUrl.startsWith("/") &&
      !returnUrl.startsWith("//") &&
      !returnUrl.includes("://");
    if (!isSafe) return;
    localStorage.removeItem(returnKey);
    navigate({ to: returnUrl });
  }, [isAlreadyProcessed, channelSelection, state, navigate]);

  // EPD-6938: render guard placed after all hooks. When the parent route's
  // beforeLoad guarantee breaks (~4% of DD samples land on /oauth/callback
  // per the issue tags), bail early instead of dereferencing organization.id
  // in JSX or callbacks below.
  if (!ensureOrgContext(organization, "oauth/callback.tsx#OAuthCallbackPage")) {
    return null;
  }

  // Detect state expiry errors for user-friendly messaging
  const isStateExpired =
    callbackMutation.error instanceof Error &&
    callbackMutation.error.message.toLowerCase().includes(STATE_EXPIRED_TOKEN);

  // Show YouTube channel picker when channels are ready to select
  if (channelSelection && !pickerError) {
    return (
      <YouTubeChannelPicker
        channels={channelSelection.channels}
        tokenBundle={channelSelection.tokenBundle}
        organizationId={organization.id}
        profileId={channelSelection.profileId}
        onSuccess={() => redirectToProfiles()}
        onError={(msg) => {
          if (codeKey) sessionStorage.removeItem(`${codeKey}_yt`);
          setPickerError(msg);
        }}
      />
    );
  }

  // Derive display status from state
  const isConnected =
    callbackMutation.isSuccess && callbackMutation.data?.type === "connected";

  const status =
    hasOAuthError ||
    hasMissingParams ||
    isAlreadyProcessed ||
    callbackMutation.isError ||
    pickerError
      ? "error"
      : isConnected
        ? "success"
        : "processing";

  const errorMessage = isStateExpired
    ? STATE_EXPIRED_MESSAGE
    : pickerError
      ? getUserFriendlyOAuthError(pickerError)
      : hasOAuthError
        ? getUserFriendlyOAuthError(
            urlErrorMessage || "Failed to connect account",
          )
        : hasMissingParams
          ? "Something went wrong — we didn't receive the expected information from the social platform. Please go back and try again."
          : isAlreadyProcessed
            ? "This connection has already been completed. If your account doesn't appear, please try connecting again."
            : callbackMutation.isError
              ? getUserFriendlyOAuthError(
                  (callbackMutation.error as Error)?.message ||
                    "Failed to connect account",
                )
              : null;

  return (
    <OAuthCallbackCard
      status={status}
      errorMessage={errorMessage}
      isStateExpired={isStateExpired}
      backLabel={isStateExpired ? "Try again" : "Go back to profiles"}
      backTo="/o/$orgSlug/profiles"
      backParams={{ orgSlug: organization.slug }}
    />
  );
}
